import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'

const ME2_EDIT_PUSH_TIMEOUT_MS = 10_000

/**
 * Which authored field the dashboard changed, for the dual-write breakdown.
 * `schedule` is absent by design: a reschedule commits through `/schedule`, which
 * mints its own kind server-side.
 */
export type Me2EditKind = 'content' | 'subject' | 'audience'

/**
 * Best-effort push of an edited custom campaign's desired state to ME2. Runs
 * after the ME1 edit commits; failures are logged, never blocking (ME1 is the
 * source of truth).
 * @param campaignId the one-time campaign being edited
 * @param edit the convergent desired state
 * @param edit.subject the edited email subject line
 * @param edit.locationId the edited audience location scope (null = brand-wide)
 * @param editKind which authored field changed, for the dual-write breakdown
 */
export async function sendMe2ConvergentEdit(
  campaignId: string,
  edit: { subject?: string; locationId?: string | null },
  editKind: Me2EditKind
): Promise<void> {
  // Per-save id, so a retry of the same click is distinguishable from a second edit.
  const editId = crypto.randomUUID()

  try {
    await OlympusClient.marketing.v1.campaigns.campaign.editMe2CustomCampaign({
      body: { ...edit, editId, editKind },
      params: { campaignId },
      signal: AbortSignal.timeout(ME2_EDIT_PUSH_TIMEOUT_MS),
    })
  } catch (error) {
    logError(error, {
      campaignId,
      convergenceFailed: true,
      editId,
      editKind,
      feature: 'me2-convergent-edit',
    })
  }
}
